import { ICommandBarItemProps, ITheme, mergeStyleSets, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPLink,
  IPMessageBar,
  IPMessageBarButton,
  IPMessageBarType,
  IPSearchBox,
  IPSeparator,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPFontIcon } from 'baseComponents/FontIcon/IPFontIcon';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTooltipHost } from 'baseComponents/TooltipHost/IPTooltipHost';
import AddEditRemoveButtons from 'components/Buttons/AddEditRemoveButtons';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import AutoSaveStatusLabel, { AutoSaveStatus } from 'components/Notification/AutoSaveStatusLabel';
import FormArchiveModal from 'components/Tasks/Forms/FormArchiveModal';
import FormStructureConflictModal from 'components/Tasks/Forms/FormStructureConflictModal';
import FormUpgradeModal from 'components/Tasks/Forms/FormUpgradeModal';
import TaskTypeFormEditor from 'components/Tasks/Forms/TaskTypeFormEditor';
import FormVersionHistoryModal from 'components/Tasks/Forms/FormVersionHistoryModal';
import {
  buildFormDiscardCommand,
  prepareDesignTimeFormElements,
} from 'components/Tasks/Forms/taskTypeFormEditor.helpers';
import { useFormStructureAutosave } from 'components/Tasks/Forms/useFormStructureAutosave';
import {
  archivedIcon,
  globalPaddingScene,
  globalStackItemStylesPaddingScene,
  globalStackTokensGapMedium,
} from 'globalStyles';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { PermissionTypes } from 'models/auth/rolePermission';
import KPI from 'models/kpi/kpi';
import TaskType from 'models/tasks/taskType';
import { TaskType_Translation } from 'models/tasks/taskType_Translation';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AddTaskTypePanel } from 'scenes/Admin/ContentConfig/Forms/AddTaskTypePanel';
import { EditTaskTypePanel } from 'scenes/Admin/ContentConfig/Forms/EditTaskTypePanel';
import FormsList from 'scenes/Admin/ContentConfig/Forms/FormsList';
import { apiGetKPIsAndFields } from 'services/Api/kpiService';
import {
  apiAddTaskType,
  apiArchiveForm,
  apiGetFormArchivePreview,
  apiGetTaskTypeForm,
  apiGetVersionedForms,
  apiRemoveTaskType,
  apiUnarchiveForm,
  apiUpdateTaskType,
} from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission, hasUserRolePermission } from 'services/Auth/featurePermissions';
import { sortOnString } from 'utils/sorting';

// The state markers behind the form name - archived, managed - are one row of pills, so they share
// one style. The app has no generic badge component; the shape below is the one the managed marker
// already used here.
const getStyles = (theme: ITheme) =>
  mergeStyleSets({
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      columnGap: '4px',
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 600,
      color: theme.palette.neutralSecondary,
      backgroundColor: theme.palette.neutralLighter,
      borderRadius: 10,
      padding: '2px 8px',
    },
  });

// A link to one form: /admin/content/forms?edit={id}. Global search, a Teams card, a rich-text
// link and the field usage list all build it through getEntityUrl.
const getRequestedFormId = (search: string): number | undefined => {
  const requested = new URLSearchParams(search).get('edit');
  const formId = Number.parseInt(requested ?? '');

  return Number.isNaN(formId) ? undefined : formId;
};

const TabForms = () => {
  const { t } = useTranslation(['fieldsLibrary', 'kpis', 'translation', 'forms', 'classifications']);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const styles = getStyles(theme);
  const { search } = useLocation();

  const [forms, setForms] = useState<TaskType[]>([]);
  const [formElements, setFormElements] = useState<TaskTypeFormElement[]>([]);
  const [kpis, setKPIs] = useState<KPI[]>([]);
  const [loadingForms, setLoadingForms] = useState<boolean>(true);
  const [loadFormsError, setLoadFormsError] = useState<boolean>(false);
  const [loadingFields, setLoadingFields] = useState<boolean>(false);
  // Forms rail management.
  const [taskTypeToEdit, setTaskTypeToEdit] = useState<TaskType | undefined>(undefined);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [showDeleteForms, setShowDeleteForms] = useState<boolean>(false);
  const [selectedFormIds, setSelectedFormIds] = useState<number[]>([]);
  // Forms waiting for a replacement/detach decision; the archive modal shows the head of the queue.
  // The edit panel's archive is a queue of one, a delete of producer-bound forms queues them all.
  const [archiveQueue, setArchiveQueue] = useState<TaskType[]>([]);
  // The family whose open tasks the upgrade modal offers to move to the current version.
  const [upgradeFamilyId, setUpgradeFamilyId] = useState<number | undefined>(undefined);
  // The family whose used head this session's autosave forked: its open tasks stay on the previous
  // version until the user updates them, which the bar under the form name offers.
  const [versionCreatedFamilyId, setVersionCreatedFamilyId] = useState<number | undefined>(undefined);
  const [showFormHistory, setShowFormHistory] = useState<boolean>(false);
  const [showDiscardChanges, setShowDiscardChanges] = useState<boolean>(false);
  const [showStructureConflict, setShowStructureConflict] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [formSearch, setFormSearch] = useState<string>('');

  // The form whose elements were applied straight from an accepted mutation, so the selection effect
  // knows not to read it back.
  const appliedFormIdRef = useRef<number | undefined>(undefined);
  // The query string the first load has read, so only a later one moves the selection.
  const consumedSearchRef = useRef<string>(search);

  const selectedFormId = selectedFormIds.length > 0 ? selectedFormIds[selectedFormIds.length - 1] : undefined;
  const selectedForm = forms.find((f) => f.taskTypeId === selectedFormId);
  // Managed (system) forms: their fields are editable like legacy (gated only by permission); only
  // the form itself is not deletable and its metadata is not editable (see canEditForm). Adding
  // attachment/context fields is still blocked for system forms (see addOptions).
  const editable =
    !!selectedForm &&
    !selectedForm.isArchived &&
    hasUserDataPermission(appContext, [selectedForm.authSchemaId], AuthSchemaLineOperation.Update);
  const canCreateForm = hasUserRolePermission(appContext, PermissionTypes.CreateForm);
  const canEditForm = (f: TaskType): boolean =>
    !f.systemTaskType &&
    !f.isArchived &&
    hasUserDataPermission(appContext, [f.authSchemaId], AuthSchemaLineOperation.Update);
  const canDeleteForm = (f: TaskType): boolean =>
    !f.systemTaskType && hasUserDataPermission(appContext, [f.authSchemaId], AuthSchemaLineOperation.Delete);
  const canManageFormFamily = (f: TaskType): boolean =>
    hasUserDataPermission(appContext, [f.authSchemaId], AuthSchemaLineOperation.Update);

  useEffect(() => {
    void loadForms(getRequestedFormId(search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A second link arrives while the tab is open: picking another form in the global search changes
  // the query string and nothing remounts. The forms are loaded by then, so the selection moves
  // without another round trip, and only when the query itself changes, so choosing a form in the
  // rail is not undone by the query the tab opened with.
  useEffect(() => {
    if (search === consumedSearchRef.current) return;

    const formId = getRequestedFormId(search);
    if (formId === undefined) {
      consumedSearchRef.current = search;

      return;
    }

    const form = forms.find((f) => f.taskTypeId === formId || f.taskTypeFamilyId === formId);
    if (!form) return;
    consumedSearchRef.current = search;
    if (form.taskTypeId === selectedFormId) return;

    //the rail shows one set at a time, so an archived form also turns that view on, and both only
    //when the editor has nothing left to save
    void openAfterAutosave(() => {
      setShowArchived(form.isArchived);
      setSelectedFormIds([form.taskTypeId]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // A form whose elements an accepted mutation has just handed us is NOT read back: they are already
  // on screen and identity-rebased. Without this the pane refetched itself every time a save forked a
  // new version - a new taskTypeId lands here - on top of the read applyAcceptedForm did itself.
  // The marker is consumed on every run, so leaving the form and coming back still reads it.
  useEffect(() => {
    const appliedFormId = appliedFormIdRef.current;
    appliedFormIdRef.current = undefined;
    if (selectedFormId !== undefined && selectedFormId !== appliedFormId) loadForm(selectedFormId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormId]);

  // The rail shows ONE set at a time - the active forms, or the archived ones with the toggle on -
  // so a new list also decides what the pane shows, in one place: the form the caller asked to stay
  // on (the one just unarchived), else the current selection while it is still in that set, else the
  // first form there is. A view with nothing left in it falls back to the active forms, so the toggle
  // can never strand the user on an empty rail.
  const applyForms = (taskTypes: TaskType[], archivedView: boolean, selectFormId?: number) => {
    // The caller can ask for one form: the one just unarchived, or the one a link names. A link
    // carries the id of a version, so a family matches as well, and an archived form turns the
    // archived view on, since the rail shows one set at a time.
    const requested =
      selectFormId !== undefined
        ? taskTypes.find((f) => f.taskTypeId === selectFormId || f.taskTypeFamilyId === selectFormId)
        : undefined;
    const view = requested ? requested.isArchived : archivedView && taskTypes.some((form) => form.isArchived);
    const visible = taskTypes.filter((form) => form.isArchived === view);
    // "The first form there is" means the first one the user SEES. The rail sorts on the localized
    // name, while the payload arrives in the server's own order - by a family's arbitrary translation
    // row - so taking the payload's head landed the selection on a form somewhere down the rail.
    // Same comparator as FormsList, on a copy: sort() mutates, and taskTypes is about to become state.
    const first = [...visible].sort((a, b) => sortOnString(a.name, b.name))[0];

    setForms(taskTypes);
    setShowArchived(view);
    setSelectedFormIds((current) => {
      if (requested) {
        return [requested.taskTypeId];
      }

      const kept = current.filter((id) => visible.some((form) => form.taskTypeId === id));
      if (kept.length > 0) {
        return kept;
      }

      return first ? [first.taskTypeId] : [];
    });
  };

  // Always read both sets: the toggle then only filters what is already here, and "is anything
  // archived at all?" - which decides whether the toggle is offered - can be answered without a
  // second round trip.
  const loadForms = async (selectFormId?: number, archivedView: boolean = showArchived): Promise<TaskType[]> => {
    try {
      setLoadingForms(true);
      setLoadFormsError(false);
      await appContext.globalDataCache.lists.getItems();
      await appContext.globalDataCache.tags.getItems();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const allKpis = await apiGetKPIsAndFields(accessToken, appContext.globalDataCache);
      const taskTypes = await apiGetVersionedForms(true, accessToken);
      setKPIs(allKpis);
      applyForms([...taskTypes], archivedView, selectFormId);

      return taskTypes;
    } catch (err) {
      setLoadFormsError(true);
      appContext.setError(err);

      return [];
    } finally {
      setLoadingForms(false);
    }
  };

  const loadForm = async (key: number) => {
    try {
      setLoadingFields(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const elements = await apiGetTaskTypeForm(key, accessToken);
      setFormElements(prepareDesignTimeFormElements(elements));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setLoadingFields(false);
    }
  };

  const persist = (newElements: TaskTypeFormElement[]) => {
    setFormElements(newElements);
    formAutosave.enqueue(newElements);
  };

  //
  // Forms rail: create / edit metadata / bulk delete (reusing the app's form panels + APIs).
  //
  const onAddForm = async (item: TaskType) => {
    try {
      appContext.showContentLoader();
      item.trans = [new TaskType_Translation()];
      item.transIdx = 0;
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newTaskType = await apiAddTaskType(item, accessToken);
      appContext.globalDataCache.taskTypes.add(newTaskType);
      setForms((cur) => [...cur, newTaskType]);
      //a new form is an active one: showing it means leaving the archived view
      setShowArchived(false);
      selectForm(newTaskType);
      setShowAddForm(false);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const onEditFormMetadata = async (item: TaskType) => {
    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const updated = await apiUpdateTaskType(item, accessToken);
      applyAcceptedForm(updated, formElements);
      setShowEditForm(false);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const onDeleteSelectedForms = async () => {
    const saved = await formAutosave.flush();
    if (!saved) {
      appContext.showNotification(t('forms:Versioning.Autosave.UnsavedBlocked'), true, false, true);

      return;
    }

    setShowDeleteForms(false);
    const deletableForms = selectedFormIds
      .map((id) => forms.find((x) => x.taskTypeId === id))
      .filter((f): f is TaskType => (f ? canDeleteForm(f) : false));
    if (deletableForms.length === 0) return;
    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      //a form whose family still has producers (templates, series or events that create work with it)
      //cannot be deleted outright: it goes to the archive modal, which shows what the archive detaches,
      //instead of a bare conflict error. A form without a family id has no producers by definition.
      const previews = await Promise.all(
        deletableForms.map(async (form) => {
          const preview = form.taskTypeFamilyId
            ? await apiGetFormArchivePreview(form.taskTypeFamilyId, accessToken)
            : undefined;
          const blocked =
            !!preview &&
            preview.producers.length + preview.inaccessibleProducerCount + preview.blockingConcreteTaskCount > 0;

          return { form, blocked };
        }),
      );
      const cleanForms = previews.filter((p) => !p.blocked).map((p) => p.form);
      const blockedForms = previews.filter((p) => p.blocked).map((p) => p.form);
      //the deletes are independent, so remove the forms in parallel; allSettled so the forms that did
      //delete leave the rail even when another one fails
      const results = await Promise.allSettled(cleanForms.map((f) => apiRemoveTaskType(f.taskTypeId, accessToken)));
      const deleted = cleanForms.filter((_, index) => results[index].status === 'fulfilled');
      deleted.forEach((f) => appContext.globalDataCache.taskTypes.remove(f));
      const deletedIds = deleted.map((f) => f.taskTypeId);
      const remaining = forms.filter((f) => !deletedIds.includes(f.taskTypeId));
      setForms(remaining);
      if (selectedFormId !== undefined && deletedIds.includes(selectedFormId)) {
        setSelectedFormIds(remaining.length > 0 ? [remaining[0].taskTypeId] : []);
      } else {
        setSelectedFormIds((currentIds) => currentIds.filter((id) => !deletedIds.includes(id)));
      }
      const firstFailure = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (firstFailure) {
        appContext.setError(firstFailure.reason);
      }
      if (blockedForms.length > 0) {
        setArchiveQueue(blockedForms);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  // `reload` re-reads the form after applying it. An autosaved structure change does NOT need it - the
  // accepted elements ARE the new form, and reading them back only blanked the designer for a spinner
  // a second after every drag. A KPI or definition commit keeps it: there the server does more than
  // accept what was posted (it forks revisions and re-projects), so the read is what makes that visible.
  const applyAcceptedForm = (taskType: TaskType, acceptedElements: TaskTypeFormElement[], reload = true) => {
    appContext.globalDataCache.taskTypes.update(taskType);
    setForms((current) => {
      const index = current.findIndex(
        (form) => form.taskTypeId === taskType.taskTypeId || form.taskTypeFamilyId === taskType.taskTypeFamilyId,
      );
      if (index < 0) {
        return [...current, taskType];
      }

      return current.map((form, currentIndex) => (currentIndex === index ? taskType : form));
    });
    appliedFormIdRef.current = taskType.taskTypeId;
    setSelectedFormIds([taskType.taskTypeId]);
    setFormElements(prepareDesignTimeFormElements(acceptedElements));
    if (reload) void loadForm(taskType.taskTypeId);
  };

  // Archiving is one quick call: no blocking overlay, and the pane keeps the form it is showing until
  // the reloaded list replaces it. Errors are reported in the archive modal, which stays open on
  // failure.
  const archiveForm = async (form: TaskType) => {
    const familyId = form.taskTypeFamilyId;
    if (!familyId) {
      return;
    }

    const saved = await formAutosave.flush();
    if (!saved) {
      appContext.showNotification(t('forms:Versioning.Autosave.UnsavedBlocked'), true, false, true);

      return;
    }

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    await apiArchiveForm(familyId, accessToken);
    //the form selectors (task top bar, templates) read the global cache, which the server fills with
    //active heads only: take the archived head out, as the delete path does
    appContext.globalDataCache.taskTypes.remove(form);
    //the server took the form off the templates and series bound to it: a template kept in the cache
    //with the old binding would render the archived form on a new task and fail on save
    await appContext.globalDataCache.templates.getItems(true);
    await loadForms();
  };

  // Unarchiving hands the user back the form they just brought back - in the active view, where it
  // now lives.
  const unarchiveSelectedForm = async () => {
    const familyId = selectedForm?.taskTypeFamilyId;
    if (!selectedForm || !familyId) {
      return;
    }

    try {
      const saved = await formAutosave.flush();
      if (!saved) {
        return;
      }

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiUnarchiveForm(familyId, accessToken);
      const taskTypes = await loadForms(familyId, false);
      //the head is selectable again: put the reloaded row back in the global cache
      const head = taskTypes.find((f) => f.taskTypeFamilyId === familyId);
      if (head) appContext.globalDataCache.taskTypes.add(head);
    } catch (error) {
      appContext.setError(error);
    }
  };

  // Dismiss covers both outcomes of the archive modal - archived, or the user skipping this form -
  // so advancing the queue here is the single exit for either.
  const advanceArchiveQueue = () => {
    setArchiveQueue((queue) => queue.slice(1));
  };

  const formAutosave = useFormStructureAutosave({
    taskType: selectedForm,
    onAccepted: (result, acceptedElements) => {
      applyAcceptedForm(result.taskType, acceptedElements, false);
      // the first structural edit of a used head forks a new version; its open tasks stay behind
      if (result.createdVersion) setVersionCreatedFamilyId(result.taskType.taskTypeFamilyId);
    },
    onError: (error) => appContext.setError(error),
  });

  useEffect(() => {
    if (formAutosave.failure === 'conflict') {
      setShowStructureConflict(true);
    }
  }, [formAutosave.failure]);

  // The version-created bar is cleared here and not from an effect on the selection: an accepted
  // fork selects the new version's id in the same render batch that sets the bar.
  const selectForm = (form?: TaskType) => {
    void formAutosave.flush().then((saved) => {
      if (saved) {
        setVersionCreatedFamilyId(undefined);
        setSelectedFormIds(form ? [form.taskTypeId] : []);
      }
    });
  };

  const updateSelectedForms = (selectedForms: TaskType[]) => {
    const nextSelectedIds = selectedForms.map((form) => form.taskTypeId);
    void formAutosave.flush().then((saved) => {
      if (!saved) {
        return;
      }

      setVersionCreatedFamilyId(undefined);
      setSelectedFormIds((currentSelectedIds) => {
        const sameSelection =
          currentSelectedIds.length === nextSelectedIds.length &&
          currentSelectedIds.every((id, index) => id === nextSelectedIds[index]);

        return sameSelection ? currentSelectedIds : nextSelectedIds;
      });
    });
  };

  // 'pending' is still a write in flight from the user's point of view - the debounce has not fired
  // yet - so it shows the same spinner as 'saving', with its own wording.
  const getAutosaveStatus = (): AutoSaveStatus => {
    switch (formAutosave.state) {
      case 'pending':
      case 'saving':
        return 'saving';
      case 'error':
        return 'error';
      default:
        return 'saved';
    }
  };

  const openAfterAutosave = async (open: () => void) => {
    if (await formAutosave.flush()) {
      open();
    }
  };

  // The discard response is the raw server projection WITHOUT the KPI graph, so applying it leaves
  // every KPI question rendering empty (SingleFormElement needs element.kpi). Read the form back to
  // restore the full elements; the head keeps its id on a discard, it only rewinds its content.
  const discardSessionChanges = async () => {
    setShowDiscardChanges(false);
    const discarded = await formAutosave.discardSession();
    const currentTaskTypeId = formAutosave.getCurrentTaskType()?.taskTypeId ?? selectedFormId;
    if (discarded && currentTaskTypeId !== undefined) {
      await loadForm(currentTaskTypeId);
    }
  };

  // Show-archived is a view option, so it belongs on the command bar next to Add/Edit/Remove, in the
  // same shape the risk, asset and objective registers use: one toggle whose label flips and whose
  // checked state renders it pressed. It SWAPS the rail to the archived forms rather than adding them
  // to the active ones - an archived form is a different thing to look at, not one more form - and it
  // is offered only while there is something archived to show.
  const getExtraCommandBarButtons = (): ICommandBarItemProps[] => [
    {
      key: 'archived',
      text: showArchived ? t('translation:ArchivedStatus.HideArchived') : t('translation:ArchivedStatus.ShowArchived'),
      checked: showArchived,
      disabled: !hasArchivedForms,
      iconProps: archivedIcon,
      onClick: () => void openAfterAutosave(() => setShowArchived(!showArchived)),
    },
    {
      key: 'upgradeTasks',
      text: t('forms:Versioning.Upgrade.Command'),
      iconProps: { iconName: 'Sync' },
      disabled: !canUpgradeSelectedForm,
      onClick: () => void openAfterAutosave(() => setUpgradeFamilyId(selectedForm?.taskTypeFamilyId)),
    },
  ];

  // Archive lives in the edit panel's footer, so the panel has to close before the archive modal
  // opens: a GenericModal rendered over an open Fluent panel light-dismisses on the first click.
  const startArchiveFromEditPanel = (form: TaskType) => {
    setShowEditForm(false);
    if (form.isArchived) {
      void unarchiveSelectedForm();
    } else {
      void openAfterAutosave(() => setArchiveQueue([form]));
    }
  };

  const hasArchivedForms = forms.some((f) => f.isArchived);
  // One active, versioned form the user may manage: the upgrade moves its open tasks to the head.
  const canUpgradeSelectedForm =
    selectedFormIds.length === 1 &&
    !!selectedForm?.taskTypeFamilyId &&
    !selectedForm.isArchived &&
    canManageFormFamily(selectedForm);
  const displayedForms = forms.filter(
    (f) => f.isArchived === showArchived && f.name.toLowerCase().includes(formSearch.trim().toLowerCase()),
  );

  const editTarget = selectedForm;
  // Only offer Delete for selected forms the user can actually delete (non-system + Delete permission),
  // mirroring TabTaskTypes. The selection itself only excludes system forms, so a user with Update but
  // not Delete could otherwise be shown an enabled Delete that the server rejects.
  const deletableSelectedIds = selectedFormIds.filter((id) => {
    const f = forms.find((x) => x.taskTypeId === id);

    return f ? canDeleteForm(f) : false;
  });

  // The modals live OUTSIDE the scene's stack: a child of a stack with childrenGap gets a gap of its
  // own, so every open modal would shift the layout behind it even though the modal itself is
  // positioned over the page.
  return (
    <>
      <IPStack
        verticalFill
        tokens={globalStackTokensGapMedium}
        styles={{ root: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } }}
      >
        <IPStack.Item styles={globalStackItemStylesPaddingScene}>
          <AddEditRemoveButtons
            overrideManager
            noLeftPadding
            onClickNew={() => setShowAddForm(true)}
            newDisabled={!canCreateForm}
            onClickEdit={() => {
              if (editTarget) {
                setTaskTypeToEdit(editTarget);
                setShowEditForm(true);
              }
            }}
            editDisabled={!editTarget || !canManageFormFamily(editTarget) || selectedFormIds.length > 1}
            onClickRemove={() => void openAfterAutosave(() => setShowDeleteForms(true))}
            removeDisabled={deletableSelectedIds.length === 0}
            extraCommandButtonProps={getExtraCommandBarButtons()}
          />
        </IPStack.Item>

        <IPStack.Item grow styles={{ root: { minHeight: 0, height: '100%', paddingRight: globalPaddingScene } }}>
          <IPStack horizontal verticalFill styles={{ root: { height: '100%', minHeight: 0, position: 'relative' } }}>
            <IPStack.Item styles={{ root: { width: 280, minWidth: 0 } }}>
              <IPStack verticalFill styles={{ root: { height: '100%' } }}>
                <IPStack.Item
                  styles={{
                    root: {
                      minHeight: 48,
                      paddingLeft: globalPaddingScene,
                      paddingRight: 8,
                    },
                  }}
                >
                  <IPSearchBox
                    underlined
                    autoFocus={!appContext.isMobileView}
                    placeholder={t('fieldsLibrary:Forms.Search')}
                    value={formSearch}
                    onChange={(_, v) => setFormSearch(v ?? '')}
                  />
                </IPStack.Item>
                <IPStack.Item grow>
                  <FormsList
                    forms={displayedForms}
                    selectedFormIds={selectedFormIds}
                    isLoading={loadingForms}
                    loadError={loadFormsError}
                    onSelectForms={updateSelectedForms}
                  />
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>

            <IPSeparator vertical />

            <IPStack.Item grow styles={{ root: { minWidth: 0, overflow: 'hidden' } }}>
              <IPStack verticalFill styles={{ root: { height: '100%' } }}>
                {/* The control surface of the selected form: its name and state badges on the left, the
                  autosave state, its recovery buttons and the version history link on the right. */}
                <IPStack.Item
                  styles={{
                    root: {
                      minHeight: 48,
                      paddingLeft: 24,
                      paddingRight: 16,
                    },
                  }}
                >
                  <IPStack
                    horizontal
                    verticalAlign="center"
                    horizontalAlign="space-between"
                    tokens={{ childrenGap: 10 }}
                  >
                    <IPStack.Item shrink styles={{ root: { minWidth: 0 } }}>
                      <IPStack horizontal verticalAlign="center" styles={{ root: { minWidth: 0, columnGap: '10px' } }}>
                        <IPText
                          styles={{
                            root: {
                              fontSize: 20,
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                          }}
                        >
                          {selectedForm?.name ?? ''}
                        </IPText>
                        {selectedForm?.isArchived && (
                          <IPText className={styles.badge}>{t('forms:Versioning.Archived')}</IPText>
                        )}
                        {selectedForm?.systemTaskType && (
                          <IPTooltipHost content={t('forms:Errors.SystemTaskTypeDelete')}>
                            <IPText className={styles.badge}>
                              <IPFontIcon iconName="Lock" />
                              {t('fieldsLibrary:Managed')}
                            </IPText>
                          </IPTooltipHost>
                        )}
                      </IPStack>
                    </IPStack.Item>
                    {selectedForm && (
                      <IPStack.Item shrink={false}>
                        <IPStack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                          <AutoSaveStatusLabel
                            status={getAutosaveStatus()}
                            savingText={
                              formAutosave.state === 'pending'
                                ? t('forms:Versioning.Autosave.Pending')
                                : t('forms:Versioning.Autosave.Saving')
                            }
                            savedText={t('forms:Versioning.Autosave.Saved')}
                            errorText={t('forms:Versioning.Autosave.Error')}
                          />
                          {formAutosave.failure === 'transport' && (
                            <IPDefaultButton
                              iconProps={{ iconName: 'Refresh' }}
                              text={t('forms:Versioning.Autosave.Retry')}
                              onClick={() => void formAutosave.retry()}
                            />
                          )}
                          {formAutosave.failure === 'conflict' && (
                            <IPDefaultButton
                              iconProps={{ iconName: 'Compare' }}
                              text={t('forms:Versioning.Conflict.Resolve')}
                              onClick={() => setShowStructureConflict(true)}
                            />
                          )}
                          <IPText styles={{ root: { whiteSpace: 'nowrap' } }}>
                            <IPLink
                              disabled={!selectedForm.taskTypeFamilyId}
                              onClick={() => void openAfterAutosave(() => setShowFormHistory(true))}
                            >
                              {t('forms:Versioning.Version', { version: selectedForm.version })}
                            </IPLink>
                          </IPText>
                        </IPStack>
                      </IPStack.Item>
                    )}
                  </IPStack>
                </IPStack.Item>
                {selectedForm &&
                  versionCreatedFamilyId !== undefined &&
                  versionCreatedFamilyId === selectedForm.taskTypeFamilyId && (
                    <IPStack.Item styles={{ root: { paddingLeft: 24, paddingRight: 16, paddingBottom: 8 } }}>
                      <IPMessageBar
                        messageBarType={IPMessageBarType.info}
                        isMultiline={true}
                        onDismiss={() => setVersionCreatedFamilyId(undefined)}
                        actions={
                          <div>
                            <IPMessageBarButton
                              onClick={() =>
                                void openAfterAutosave(() => setUpgradeFamilyId(selectedForm.taskTypeFamilyId))
                              }
                            >
                              {t('forms:Versioning.Upgrade.Command')}
                            </IPMessageBarButton>
                          </div>
                        }
                      >
                        {t('forms:Versioning.Upgrade.VersionCreated', { version: selectedForm.version })}
                      </IPMessageBar>
                    </IPStack.Item>
                  )}
                <IPStack.Item
                  grow
                  styles={{
                    root: { position: 'relative', height: '100%', minHeight: 0, overflowY: 'auto', paddingLeft: 24 },
                  }}
                >
                  {selectedFormId === undefined ? (
                    <IPText
                      styles={{ root: { color: theme.palette.neutralSecondary, paddingTop: 56, textAlign: 'center' } }}
                    >
                      {t('fieldsLibrary:Detail.SelectForm')}
                    </IPText>
                  ) : loadingFields ? (
                    <IPStack horizontalAlign="center" styles={{ root: { paddingTop: 40 } }}>
                      <IPSpinner size={IPSpinnerSize.medium} />
                    </IPStack>
                  ) : (
                    <TaskTypeFormEditor
                      taskTypeId={selectedFormId}
                      formTaskType={selectedForm}
                      getCurrentFormTaskType={formAutosave.getCurrentTaskType}
                      kpis={kpis}
                      onKPIsChange={setKPIs}
                      formElements={formElements}
                      editable={editable}
                      readonly={!editable}
                      isSystemForm={!!selectedForm?.systemTaskType}
                      isLoading={loadingFields}
                      compact={true}
                      hideEmptyFormText={true}
                      designTime={true}
                      showFieldCards={true}
                      fieldPanelAsPanel={true}
                      formFieldModalAsPanel={true}
                      persistPreviewDismissed={true}
                      toolbarCommands={[
                        buildFormDiscardCommand(t, formAutosave.canDiscard, () => setShowDiscardChanges(true)),
                      ]}
                      onElementsChange={(next, { persist: shouldPersist }) =>
                        shouldPersist ? persist(next) : setFormElements(next)
                      }
                      onStructureCommitted={(taskType, elements) => {
                        formAutosave.rebase(taskType);
                        applyAcceptedForm(taskType, elements);
                      }}
                      onBeforeDefinitionCommit={formAutosave.flush}
                      onDefinitionCommitted={(result) => {
                        const current = result.updatedTaskTypes.find(
                          (taskType) => taskType.taskTypeFamilyId === selectedForm?.taskTypeFamilyId,
                        );
                        if (current) {
                          formAutosave.rebase(current);
                          applyAcceptedForm(current, formElements);
                        }
                      }}
                      confirmFieldRemoval={true}
                    />
                  )}
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>

      {showAddForm && (
        <AddTaskTypePanel isOpen={showAddForm} onDismiss={() => setShowAddForm(false)} onAddNewTaskType={onAddForm} />
      )}
      {showEditForm && taskTypeToEdit && (
        <EditTaskTypePanel
          isOpen={showEditForm}
          onDismiss={() => setShowEditForm(false)}
          onEditTaskType={onEditFormMetadata}
          onEditForm={(item) => {
            setShowEditForm(false);
            selectForm(item);
          }}
          taskTypeToEdit={taskTypeToEdit}
          metadataReadOnly={!canEditForm(taskTypeToEdit)}
          canArchive={canManageFormFamily(taskTypeToEdit) && !!taskTypeToEdit.taskTypeFamilyId}
          isArchived={taskTypeToEdit.isArchived}
          onArchive={() => startArchiveFromEditPanel(taskTypeToEdit)}
        />
      )}
      {showDeleteForms && (
        <DialogConfirmDelete
          hidden={!showDeleteForms}
          title={t('fieldsLibrary:Forms.DeleteTitle')}
          subText={t('fieldsLibrary:Forms.DeleteText', { count: deletableSelectedIds.length })}
          onNo={() => setShowDeleteForms(false)}
          onYes={onDeleteSelectedForms}
        />
      )}
      {showDiscardChanges && (
        <DialogYesNo
          hidden={!showDiscardChanges}
          title={t('forms:Versioning.Autosave.DiscardTitle')}
          subText={t('forms:Versioning.Autosave.DiscardText')}
          yesText={t('translation:General.Button.Discard')}
          noText={t('translation:General.Button.Cancel')}
          onNo={() => setShowDiscardChanges(false)}
          onYes={() => void discardSessionChanges()}
        />
      )}
      {showStructureConflict && selectedForm?.taskTypeFamilyId && (
        <FormStructureConflictModal
          taskTypeFamilyId={selectedForm.taskTypeFamilyId}
          localFormElements={formElements}
          isOpen={showStructureConflict}
          onDismiss={() => setShowStructureConflict(false)}
          onReload={(taskType, elements) => {
            formAutosave.rebase(taskType);
            applyAcceptedForm(taskType, elements);
          }}
          onReapply={(taskType, elements) => formAutosave.reapply(taskType, elements)}
        />
      )}
      {archiveQueue.length > 0 && (
        <FormArchiveModal
          taskType={archiveQueue[0]}
          isOpen={true}
          onDismiss={advanceArchiveQueue}
          onArchive={() => archiveForm(archiveQueue[0])}
        />
      )}
      {upgradeFamilyId !== undefined && (
        <FormUpgradeModal
          taskTypeFamilyId={upgradeFamilyId}
          isOpen={true}
          onDismiss={() => setUpgradeFamilyId(undefined)}
          onUpgraded={() => setVersionCreatedFamilyId(undefined)}
        />
      )}
      {showFormHistory && selectedForm && (
        <FormVersionHistoryModal
          taskType={selectedForm}
          isOpen={showFormHistory}
          onDismiss={() => setShowFormHistory(false)}
          onPurged={() => void loadForms()}
          onCorrected={() => void loadForms()}
        />
      )}
    </>
  );
};

export default TabForms;
